/**
 * Domain types for the /me couple-onboarding surface.
 *
 * Ported from the prototype (`prototype-www/src/surfaces/me/types.ts`). These
 * camelCase types are the *view-layer* contract the step components consume.
 * The production wire contract is snake_case (orval-generated `CoupleOut` /
 * `AnswersPatch` / `ConsentIn` etc.); `meHooks.ts` adapts the generated
 * snake_case payloads ⇄ these camelCase view types at the boundary, so the
 * step components port verbatim against this contract.
 */

export type OnboardingStep =
  | "welcome"
  | "partner_picker"
  | "neither"
  | "identity"
  | "verify_email"
  | "relationship_status"
  | "relationship_length"
  | "children"
  | "focus_areas"
  | "vision"
  | "preview"
  | "privacy"
  | "consent"
  | "handoff"
  | "homebase";

/**
 * The option-key unions are DERIVED from the option lists in
 * `onboarding/answerVocabulary.ts`, not restated here.
 *
 * They used to be hand-written beside the lists, which made two definition sites
 * for one vocabulary: a union member could exist with no option to select it, or
 * an option could carry a key the union had never heard of, and nothing failed.
 * Re-exporting keeps every existing `from "../../types"` import working while
 * leaving exactly one place a key is declared — the place its label lives, under
 * the lock in `onboarding/answerLabelLock.ts`.
 */
import type {
  RelationshipStatus,
  RelationshipLength,
  ChildrenStatus,
  FocusArea,
} from "./onboarding/answerVocabulary";

export type { RelationshipStatus, RelationshipLength, ChildrenStatus, FocusArea };

export interface OnboardingAnswers {
  confirmedName: string | null;
  preferredName: string | null;
  timezone: string | null;
  relationshipStatus: RelationshipStatus | null;
  /** Free-text detail captured when relationshipStatus === "other". */
  relationshipStatusOther: string | null;
  relationshipLength: RelationshipLength | null;
  children: ChildrenStatus | null;
  focusAreas: FocusArea[];
  /** Free-text detail captured when focusAreas includes "something_else". */
  focusAreasOther: string | null;
  sixMonthsFromNow: string | null;
}

/** Client-entered supplementary contact (S2c). Both fields optional. */
export interface ContactDraft {
  email: string | null;
  phone: string | null;
}

export interface ConsentRecord {
  therapyConsent: boolean;
  platformConsent: boolean;
  transcriptionConsent: boolean;
  typedSignature: string;
  signedAt: string; // ISO datetime
}

export interface OnboardingState {
  step: OnboardingStep;
  furthestStep: OnboardingStep;
  answers: OnboardingAnswers;
  consent: ConsentRecord | null;
  /** Derived server-side: set once the partner's 1:1 conversation exists
   *  (i.e. they joined Dova by SMS). Ephemeral-local during the active flow. */
  completedAt: string | null;
}

export interface Partner {
  id: string;
  firstName: string;
  lastName: string;
  /** The partner's own email (full for the acting partner, masked for the other —
   *  PII default). Pre-fills the contact fields on the "A bit about you" step. */
  email: string | null;
  /** The partner's own phone (full for the acting partner, masked for the other).
   *  Pre-fills the contact fields on the "A bit about you" step; null when none on file. */
  phone: string | null;
  avatarUrl: string | null;
  onboardingState: OnboardingState;
}

export interface Couple {
  id: string;
  /** The authed person's id (person_global_uuid) — always one of `partners`.
   *  The acting partner is resolved off this, server-authoritative, value. */
  actingPartnerId: string;
  therapistId: string;
  therapistName: string;
  therapistContactEmail: string;
  /** Optional — null when the couple has not yet booked their first session.
   *  S1/S11 render nothing at all when absent. */
  firstSessionAt: string | null;
  /** How long the session runs. Null on a legacy couple: that source is a bare
   *  timestamp with no length beside it, so the card omits the fact rather than
   *  guessing at it. */
  firstSessionDurationMinutes: number | null;
  /** Where it happens, as the server's CODE (`"video_call"`), not a label. The
   *  wording is this app's to choose; which place it is, is not. */
  firstSessionLocation: string | null;
  /** The therapist's office address. Null for video and when none is on file. */
  firstSessionLocationAddress: string | null;
  partners: Partner[];
}

/** Response from the SMS handoff endpoint (snake_case `SmsHandoffOut` adapted). */
export interface SmsHandoffResponse {
  /** The single couple access code embedded in the first SMS. */
  accessCode: string;
  /** Native `sms:` deep link with prefilled body containing the access code. */
  smsUri: string;
  /** Display-friendly version of the Dova number for fallback / manual entry. */
  dovaPhoneNumber: string;
  /** The canonical handoff message — the EXACT string prefilled into `smsUri`'s body
   *  (carries `DOVA-<code>`). S9 renders this verbatim so the preview == what's sent. */
  messageBody: string;
}

export const EMPTY_ANSWERS: Readonly<OnboardingAnswers> = Object.freeze({
  confirmedName: null,
  preferredName: null,
  timezone: null,
  relationshipStatus: null,
  relationshipStatusOther: null,
  relationshipLength: null,
  children: null,
  focusAreas: Object.freeze([]) as ReadonlyArray<FocusArea>,
  focusAreasOther: null,
  sixMonthsFromNow: null,
}) as Readonly<OnboardingAnswers>;

export const EMPTY_ONBOARDING_STATE: Readonly<OnboardingState> = Object.freeze({
  step: "welcome",
  furthestStep: "welcome",
  answers: EMPTY_ANSWERS,
  consent: null,
  completedAt: null,
}) as Readonly<OnboardingState>;
